import {format} from "date-fns";
import type {NotificationConfig, SecondNotificationConfig} from "../ETANotificationConfigsProvider";
import type {OptionType} from "../../../../common/types";

// DP-3115: DISPLAY-ONLY stop-type label overrides. The stored/persisted value (matched by key,
// sent to BE) stays unchanged — only the UI shows the quoted form. Never feed these into
// selection or save; use only when rendering.
export const STOP_TYPE_DISPLAY_LABELS: Record<string, string> = {B: '"Both"'};

export const formatStopTypesForDisplay = (options: OptionType[]): OptionType[] =>
  options.map((option) => {
    const override = STOP_TYPE_DISPLAY_LABELS[String(option.key)];
    return override ? {...option, value: override} : option;
  });

// DP-3098 QA bug — trigger/time-window values past 7 digits are meaningless, and 10+-digit minute
// offsets overflow the JS Date range, making date-fns format() throw and crash the app.
export const MAX_TRIGGER_INPUT_DIGITS = 7;

export const handleNumberPaste =
  (onChange: (value: string) => void, maxLength?: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const sanitizedValue = pastedText.replace(/[^0-9]/g, "").slice(0, maxLength);
    onChange(sanitizedValue);
  };

export const handleNumberChange = (onChange: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
  onChange(e.target.value.slice(0, MAX_TRIGGER_INPUT_DIGITS));
};

export const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
};

/**
 * DP-3044 — cross-field rule for the 2nd ETA Notification's `awayFrom` value.
 *
 * When the 1st and 2nd notifications share the same trigger type, the 2nd's value
 * must be strictly fewer than the 1st's (a larger `awayFrom` fires earlier, so an
 * equal-or-larger 2nd would never fire after the 1st). The rule does NOT apply when:
 *  - the 2nd notification is disabled,
 *  - either value is empty / non-numeric (per-field required handles those),
 *  - the trigger types differ (minutes vs stops are not comparable).
 *
 * Returns `true` when valid, or the verbatim error message string when invalid.
 */
export const validateSecondNotificationOrdering = (
  first: {triggerWhen: string; awayFrom: string} | undefined,
  second: {enabled: boolean; triggerWhen: string} | undefined,
  value: unknown
): true | string => {
  if (!second?.enabled) return true;
  if (value === "" || value === null || value === undefined) return true;
  if (first?.awayFrom === "" || first?.awayFrom === null || first?.awayFrom === undefined) return true;
  if (first?.triggerWhen !== second?.triggerWhen) return true;

  // The inputs are numeric (type="number" + digit-only key/paste handlers) and empty
  // values are short-circuited above. parseFloat is still needed for a numeric (not
  // string) comparison; any NaN here makes `>=` false, which correctly falls through
  // to "valid" — so no explicit NaN guard is required.
  const firstNum = Number.parseFloat(String(first.awayFrom));
  const secondNum = Number.parseFloat(String(value));

  if (secondNum >= firstNum) {
    return second.triggerWhen === "eta"
      ? "Minutes must be fewer than 1st Notification"
      : "Stops must be fewer than 1st Notification";
  }
  return true;
};

/**
 * DP-3095 — the "15/30" Follow Up rule for a single notification type.
 *
 * A notification qualifies for follow-ups when its time window is at least 15
 * minutes before and 30 minutes after the ETA. Empty / non-numeric values fail
 * (NaN comparisons are false), so no explicit guard is needed.
 */
export const meetsFollowUpWindow = (notification: NotificationConfig | undefined): boolean => {
  const before = Number.parseFloat(notification?.timeWindowBeforeETA || "");
  const after = Number.parseFloat(notification?.timeWindowAfterETA || "");
  return before >= 15 && after >= 30;
};

export interface FollowUpEligibility {
  /** 1st notification meets the 15/30 window. */
  first: boolean;
  /** 2nd notification: true/false when enabled, null when the 2nd notification is off. */
  second: boolean | null;
  /** At least one considered type qualifies → Follow Up can be enabled. */
  anyQualifies: boolean;
  /** Every considered type qualifies → no informational popup needed. */
  allQualifies: boolean;
  /** Some — but not all — considered types qualify → show the informational popup. */
  isPartial: boolean;
}

/**
 * DP-3095 — per-type Follow Up eligibility (optimization is intentionally excluded;
 * ETA does not drift from optimization). The 2nd notification only counts when it is enabled
 * (read from `secondNotification.enabled`).
 */
export const getFollowUpEligibility = (
  firstNotification: NotificationConfig | undefined,
  secondNotification: SecondNotificationConfig | undefined
): FollowUpEligibility => {
  const first = meetsFollowUpWindow(firstNotification);
  const second = secondNotification?.enabled ? meetsFollowUpWindow(secondNotification) : null;
  const anyQualifies = first || second === true;
  const allQualifies = first && second !== false;
  return {first, second, anyQualifies, allQualifies, isPartial: anyQualifies && !allQualifies};
};

/**
 * DP-3095 — the informational popup copy naming which type will / won't get follow-ups.
 * Only meaningful for the partial case (exactly one of 1st / enabled-2nd qualifies).
 */
export const buildFollowUpPartialMessage = (eligibility: FollowUpEligibility): string => {
  const qualifying = eligibility.first ? "1st notification" : "2nd notification";
  const nonQualifying = eligibility.first ? "2nd notification" : "1st notification";
  return `We will send follow up notifications for ${qualifying} but not ${nonQualifying} because the ${nonQualifying} doesn't include time windows that are at least 15 minutes before and 30 minutes after.`;
};

/**
 * DP-3098 — the fixed example ETA used to illustrate the notification feedback timeline.
 * The visual is anchored to a constant 1:00 PM (not a real ETA).
 */
export const EXAMPLE_ETA_LABEL = "1:00 PM"; // caption text
const EXAMPLE_ETA_HOUR = 13; // 1:00 PM

// Rail geometry (percentages). Two layouts share the same scale helpers:
//   • Stop trigger — "Notification sent" is pinned far left ("X stops before" has no clock time);
//     only the clock markers (window start / ETA / window end) are placed to-scale in the window zone.
//   • ETA trigger (DP-3116) — all four markers share ONE to-scale axis over the full rail; the sent
//     dot moves to its real time (ETA − away) and merges (outlined) with a coincident clock marker.
// Adjacent labels are then decluttered so they never collide; all offsets coincident → one centered dot.
const RAIL_LEFT = 4;
const RAIL_RIGHT = 96;
export const MIN_GAP = 6; // small so proportions read true (1 min ≪ 15 min); very-close labels may sit tight
// Labels are fixed-pixel (12px, nowrap) while the gap is a % of the rail, so on narrow rails 6% is
// fewer pixels than a label needs. The component converts this pixel minimum into a % of the measured
// rail width (floored at MIN_GAP) — sized for the widest adjacent pair ("Window start"/"Window end").
export const MIN_LABEL_GAP_PX = 72;
const SENT_POS = RAIL_LEFT; // Stop trigger: "Notification sent" is pinned here.
const WINDOW_LEFT = 24; // Stop trigger: clock markers (start/eta/end) begin here, right of pinned "sent".
const COLLAPSED_POS = (RAIL_LEFT + RAIL_RIGHT) / 2; // all offsets coincident → one centered dot.

export type TimelineMarkerRole = "notificationSent" | "windowStart" | "eta" | "windowEnd";

export interface TimelineMarker {
  /** Rail position, 0–100 %. */
  position: number;
  /** Value shown above the dot: a clock time, or "Xmin before" / "X stop(s) before" for the sent dot. */
  valueLabel: string;
  /** Role labels sharing this dot (canonical order: ETA → Window start → Window end → Notification sent). */
  roleLabels: string[];
  /** Dot fill — the winning role's color (eta > window; a standalone sent dot has its own color). */
  fill: "sent" | "window" | "eta";
  /** DP-3116 — draw a black outline ring: the ETA sent dot coincides with this clock marker. */
  outlined?: boolean;
}

export interface NotificationTimelineModel {
  /** Italic helper caption shown above the rail. */
  caption: string;
  /** Dots to render, left → right; clock roles that share a time are merged into one dot. */
  markers: TimelineMarker[];
  /** Band anchors (%): lead-in draws sent → window start; active band window start → window end. */
  notificationSentPosition: number;
  windowStartPosition: number;
  windowEndPosition: number;
}

// Map a minute offset onto a rail sub-range [lo, hi]. Degenerate range (all offsets equal) → center.
const scaleToRail = (offset: number, minOffset: number, range: number, lo: number, hi: number): number =>
  range === 0 ? (lo + hi) / 2 : lo + ((offset - minOffset) / range) * (hi - lo);

// Keep an ordered position list ≥ minGap apart and within [.., hi]: push right on the forward pass,
// then pull back from the right if the last one overflowed.
const declutterPositions = (sorted: number[], hi: number, minGap: number): number[] => {
  const out = [...sorted];
  for (let i = 1; i < out.length; i++) {
    if (out[i] < out[i - 1] + minGap) out[i] = out[i - 1] + minGap;
  }
  if (out[out.length - 1] > hi) {
    out[out.length - 1] = hi;
    for (let i = out.length - 2; i >= 0; i--) {
      if (out[i] > out[i + 1] - minGap) out[i] = out[i + 1] - minGap;
    }
  }
  return out;
};

const ROLE_LABELS: Record<TimelineMarkerRole, string> = {
  notificationSent: "Notification sent",
  windowStart: "Window start",
  eta: "ETA",
  windowEnd: "Window end"
};
// Stack order within a merged dot (top → bottom). DP-3116: ETA leads — a coincident dot is the ETA
// point (rendered red), so its label sits on top — then window start / end; notificationSent is LAST so
// it renders *under* the clock role it coincides with (ticket: "Notification sent" under "Window start").
const ROLE_ORDER: TimelineMarkerRole[] = ["eta", "windowStart", "windowEnd", "notificationSent"];

interface MarkerGroup {
  offset: number;
  position: number;
  roles: TimelineMarkerRole[];
}

// Group roles that share a time into one dot, then build MarkerGroups from a caller-supplied position
// list (one per distinct offset, ascending). Pure grouping only.
const groupsFromOffsets = (
  entries: [TimelineMarkerRole, number][],
  place: (offsets: number[]) => number[]
): MarkerGroup[] => {
  const rolesByOffset = new Map<number, TimelineMarkerRole[]>();
  for (const [role, offset] of entries) {
    rolesByOffset.set(offset, [...(rolesByOffset.get(offset) ?? []), role]);
  }
  const offsets = [...rolesByOffset.keys()].sort((a, b) => a - b);
  const positions = place(offsets);
  return offsets.map((offset, i) => ({
    offset,
    position: positions[i],
    roles: (rolesByOffset.get(offset) as TimelineMarkerRole[]).sort(
      (a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b)
    )
  }));
};

/**
 * DP-3098 — unified clock-marker layout (both triggers). Window start / ETA / window end are placed
 * to-scale by their minute offset in the zone right of the pinned "sent" dot; roles sharing a time
 * merge into one dot; all three coincident (e.g. a 0/0 window) → a single centered dot.
 */
const layoutWindowMarkers = (before: number, after: number, minGap: number): MarkerGroup[] =>
  groupsFromOffsets(
    [
      ["windowStart", -before],
      ["eta", 0],
      ["windowEnd", after]
    ],
    (offsets) => {
      const minOffset = offsets[0];
      const range = offsets[offsets.length - 1] - minOffset;
      return range === 0
        ? offsets.map(() => COLLAPSED_POS)
        : declutterPositions(
            offsets.map((o) => scaleToRail(o, minOffset, range, WINDOW_LEFT, RAIL_RIGHT)),
            RAIL_RIGHT,
            minGap
          );
    }
  );

/**
 * DP-3116 — ETA-trigger layout. The "Notification sent" dot is NOT pinned: it is placed to-scale at
 * its real time (offset −away) alongside the clock markers, all four on ONE proportional axis over the
 * full rail. Roles sharing a time merge into one dot (a sent+clock merge → an outlined clock dot);
 * all offsets coincident (e.g. a 0/0 window fired at the ETA) → a single centered dot.
 */
const layoutEtaMarkers = (away: number, before: number, after: number, minGap: number): MarkerGroup[] =>
  groupsFromOffsets(
    [
      ["notificationSent", -away],
      ["windowStart", -before],
      ["eta", 0],
      ["windowEnd", after]
    ],
    (offsets) => {
      const minOffset = offsets[0];
      const range = offsets[offsets.length - 1] - minOffset;
      return range === 0
        ? offsets.map(() => COLLAPSED_POS)
        : declutterPositions(
            offsets.map((o) => scaleToRail(o, minOffset, range, RAIL_LEFT, RAIL_RIGHT)),
            RAIL_RIGHT,
            minGap
          );
    }
  );

// Build a marker from a laid-out group. A group with only "Notification sent" is the standalone black
// dot (labelled by trigger); a group with clock role(s) shows the clock time, and if the sent time
// coincides (DP-3116) it merges in as a black-outlined dot.
const markerFromGroup = (
  group: MarkerGroup,
  toClock: (offsetMinutes: number) => string,
  sentValueLabel: string
): TimelineMarker => {
  const hasSent = group.roles.includes("notificationSent");
  const clockRoles = group.roles.filter((role) => role !== "notificationSent");
  if (clockRoles.length === 0) {
    return {
      position: group.position,
      valueLabel: sentValueLabel,
      roleLabels: [ROLE_LABELS.notificationSent],
      fill: "sent"
    };
  }
  const marker: TimelineMarker = {
    position: group.position,
    valueLabel: toClock(group.offset),
    roleLabels: group.roles.map((role) => ROLE_LABELS[role]),
    fill: clockRoles.includes("eta") ? "eta" : "window"
  };
  if (hasSent) marker.outlined = true;
  return marker;
};

/**
 * DP-3098 / DP-3116 — derive the notification feedback timeline from a config, anchored on a fixed
 * example ETA of 1:00 PM. Returns null when the inputs aren't yet usable numbers (empty / NaN /
 * negative) so the caller hides the timeline until the config is valid.
 *
 * - Stop trigger: "Notification sent" is a black dot pinned far left ("{X} stop(s) before"); the clock
 *   markers are laid out to-scale in the window zone.
 * - ETA trigger (DP-3116): all four markers share one to-scale axis over the full rail; the sent dot
 *   ("{X}min before") sits at its real time and, when it coincides with a clock marker, merges into a
 *   black-outlined dot with "Notification sent" stacked under the clock role.
 * - Roles sharing a time merge into one dot; all offsets coincident collapse to a single centered dot.
 */
export const buildNotificationTimeline = (
  config: NotificationConfig | undefined,
  minGapPercent: number = MIN_GAP
): NotificationTimelineModel | null => {
  const away = Number.parseFloat(config?.awayFrom ?? "");
  const before = Number.parseFloat(config?.timeWindowBeforeETA ?? "");
  const after = Number.parseFloat(config?.timeWindowAfterETA ?? "");
  if (![away, before, after].every((n) => Number.isFinite(n) && n >= 0)) {
    return null;
  }

  const eta = new Date(2000, 0, 1, EXAMPLE_ETA_HOUR, 0, 0, 0);
  const toClock = (offsetMinutes: number) => format(new Date(eta.getTime() + offsetMinutes * 60_000), "h:mm a");
  const isEta = config?.triggerWhen === "eta";
  const sentValueLabel = isEta ? `${away}min before` : `${away} ${away === 1 ? "stop" : "stops"} before`;

  // Both triggers lay out the clock markers with the same helpers; they differ only in how the sent
  // dot is positioned (ETA: to-scale on the shared axis; Stop: pinned far left).
  const groups = isEta
    ? layoutEtaMarkers(away, before, after, minGapPercent)
    : layoutWindowMarkers(before, after, minGapPercent);
  const positionOf = (role: TimelineMarkerRole) => (groups.find((g) => g.roles.includes(role)) as MarkerGroup).position;

  let markers: TimelineMarker[];
  let notificationSentPosition: number;
  if (isEta) {
    markers = groups.map((group) => markerFromGroup(group, toClock, sentValueLabel));
    notificationSentPosition = positionOf("notificationSent");
  } else {
    markers = [
      {position: SENT_POS, valueLabel: sentValueLabel, roleLabels: [ROLE_LABELS.notificationSent], fill: "sent"},
      ...groups.map((group) => markerFromGroup(group, toClock, sentValueLabel))
    ];
    notificationSentPosition = SENT_POS;
  }

  return {
    caption: `Using an example ETA of ${EXAMPLE_ETA_LABEL}, your clients would receive a window of ${toClock(
      -before
    )} - ${toClock(after)}`,
    markers,
    notificationSentPosition,
    windowStartPosition: positionOf("windowStart"),
    windowEndPosition: positionOf("windowEnd")
  };
};
